// Themes management section - handles CRUD operations for themes
import React, { useState, useMemo, useRef } from "react";
import { useToast } from "../providers/ToastProvider";
import { useLocalization } from "../providers/LocalizationProvider";
import { useFilter } from "../lib/hooks/useFilter";
import { useEditing } from "../lib/hooks/useEditing";
import { useDataSource } from "../lib/hooks/useDataSource";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import EditorModal from "../components/ui/EditorModal";
import Confirm from "../components/ui/Confirm";
import { Input, Label } from "../components/ui/Input";
import { Checkbox } from "../components/ui/Checkbox";
import TruncatedText from "../components/ui/TruncatedText";
import ResponsiveTable from "../components/ui/ResponsiveTable";
import { useRestAuth } from "../providers/RestAuthProvider";
import UserSelect from "../components/ui/UserSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { USER_ROLES } from "../lib/config/constants";
import { logger } from "../lib/utils/logger";

/**
 * Themes Section with professional error handling and state management
 */
export default function ThemesSection({ dataSource, showTrash }) {
  // Get hooks for notifications, authentication, and localization
  const { notify } = useToast();
  const { user } = useRestAuth();
  const { t, language, userLanguageDetermined } = useLocalization();

  // Use data source hook with current language for theme operations
  const {
    data: themes,
    isLoading,
    error,
    create,
    update,
    softDelete,
    restore,
    hardDelete,
    refresh,
    clearError
  } = useDataSource(dataSource, 'themes', language, userLanguageDetermined);

  // Check if current user is admin
  const isAdmin = user?.role === USER_ROLES.ADMIN;

  // Local state
  const filter = useFilter("");
  const { editing, setEditing, openNew, openEdit, close } = useEditing({
    nom: "",
    deletedAt: null,
    userId: user?.id
  });

  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    description: "",
    withChildren: false,
    operation: "delete" // "delete" or "restore"
  });

  const cascadeRef = useRef(false);

  // Ref to preserve userId across language changes
  const preservedUserIdRef = React.useRef(editing?.userId || user?.id);

  // Update ref when editing changes
  React.useEffect(() => {
    if (editing?.userId) {
      preservedUserIdRef.current = editing.userId;
    } else if (!editing && user?.id) {
      preservedUserIdRef.current = user.id;
    }
  }, [editing?.userId, user?.id]);

  // Ensure themes are loaded on component mount when language is determined
  React.useEffect(() => {
    if (themes.length === 0 && !isLoading && userLanguageDetermined) {
      refresh(true);
    }
  }, [themes.length, isLoading, userLanguageDetermined, refresh]);

  // Note: Users data is now loaded centrally by AdminShell
  // No need for manual loading here

  // Computed values
  const filteredData = useMemo(() => {
    return themes.filter(t =>
      (showTrash ? t.deletedAt : !t.deletedAt) &&
      t.nom.toLowerCase().includes(filter.value.toLowerCase())
    );
  }, [themes, showTrash, filter.value]);

  // Memoize user names to ensure they update when users are loaded
  const userNames = React.useMemo(() => {
    const names = {};
    if (isAdmin && dataSource.state.users && dataSource.state.users.length > 0) {
      dataSource.state.users.forEach(user => {
        const firstName = user.firstName || '';
        const lastName = user.lastName || user.name || '';
        names[user.id] = [firstName, lastName].filter(Boolean).join(' ') || "—";
      });
    }
    return names;
  }, [isAdmin, dataSource.state.users]);

  // Refresh themes and questions when language changes
  // Note: App.jsx already calls updateLanguage and reload(), but we need to ensure
  // questions are reloaded for this section since they're needed for counts
  React.useEffect(() => {
    if (userLanguageDetermined && dataSource && dataSource.reload) {
      // Preserve editing state before language change
      const currentEditing = editing;
      const preservedUserId = currentEditing?.userId || preservedUserIdRef.current || user?.id;

      // Reload questions with the new language (themes will be reloaded by refresh)
      // We reload questions separately because they're language-specific and needed for counts
      dataSource.reload(['questions'], language).then(() => {
        // Restore userId in editing state after questions are reloaded
        if (currentEditing && preservedUserId) {
          setEditing({ ...currentEditing, userId: preservedUserId });
        }
      }).catch(err => {
        logger.error('Failed to reload questions on language change', err);
        // Still try to restore editing state even if reload fails
        if (currentEditing && preservedUserId) {
          setEditing({ ...currentEditing, userId: preservedUserId });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, userLanguageDetermined]);

  // Helper functions
  const userName = (uid) => {
    if (!uid) return t('common.notAssigned');
    if (!isAdmin) return t('common.notAssigned');

    // If users are not loaded yet, show loading
    if (!dataSource.state.users || dataSource.state.users.length === 0) {
      return t('common.loadingText');
    }

    // If users are loaded but this specific user is not found, show unknown
    return userNames[uid] || t('common.unknown');
  };


  const canManage = (ownerId) => isAdmin || ownerId === user?.id;

  // Event handlers
  const handleSave = async (e) => {
    e.preventDefault();
    clearError();

    const form = new FormData(e.currentTarget);
    const nom = form.get('nom') + "";
    const contentLanguageCode = form.get('contentLanguage') + "";

    const userId = form.get('userId') + "";

    try {
      if (editing?.id) {
        // PATCH only updates translation text, userId is optional
        const updatePayload = { nom };
        if (userId) {
          updatePayload.userId = userId;
        }
        await update(editing.id, updatePayload);
        notify({ type: 'success', message: t('themes.themeUpdated') });
      } else {
        await create({ nom, contentLanguageCode });
        notify({ type: 'success', message: t('themes.themeCreated') });
      }
      setEditing(null);
      await refresh(true); // Force refresh to get latest data
    } catch (error) {
      // Error is already handled by the hook
      logger.error('Save error', error);
    }
  };

  const handleDelete = (theme) => {
    const questions = dataSource.state.questions || [];
    const responses = dataSource.state.responses || [];

    const questionsToDelete = questions.filter(q =>
      q.themeId === theme.id && !q.deletedAt
    );
    const responsesToDelete = responses.filter(r =>
      !r.deletedAt && questionsToDelete.some(q => q.id === r.questionId)
    );

    const hasChildren = questionsToDelete.length > 0;
    cascadeRef.current = false;

    setConfirm({
      open: true,
      title: t('themes.deleteConfirm', { name: theme.nom }),
      description: hasChildren
        ? t('themes.deleteWithChildren', { questions: questionsToDelete.length, responses: responsesToDelete.length })
        : "",
      withChildren: hasChildren,
      operation: "delete",
      onConfirm: async () => {
        try {
          await softDelete(theme.id, cascadeRef.current);
          notify({ type: 'success', message: t('themes.themeMovedToTrash') });
          setConfirm({ open: false, title: "", description: "", withChildren: false, operation: "delete" });
          await refresh(true);
        } catch (error) {
          logger.error('Delete error', error);
        }
      },
      onCancel: () => setConfirm({
        open: false,
        title: "",
        description: "",
        withChildren: false
      })
    });
  };

  const handleRestore = (theme) => {
    const questions = dataSource.state.questions || [];
    const responses = dataSource.state.responses || [];

    const questionsToRestore = questions.filter(q =>
      q.themeId === theme.id && q.deletedAt
    );
    const responsesToRestore = responses.filter(r =>
      r.deletedAt && questionsToRestore.some(q => q.id === r.questionId)
    );

    const hasChildren = questionsToRestore.length > 0;
    cascadeRef.current = false;

    setConfirm({
      open: true,
      title: t('themes.restoreConfirm', { name: theme.nom }),
      description: hasChildren
        ? t('themes.restoreWithChildren', { questions: questionsToRestore.length, responses: responsesToRestore.length })
        : "",
      withChildren: hasChildren,
      operation: "restore",
      onConfirm: async () => {
        try {
          await restore(theme.id, cascadeRef.current);
          notify({ type: 'success', message: t('themes.themeRestored') });
          setConfirm({ open: false, title: "", description: "", withChildren: false, operation: "restore" });
          await refresh(true);
        } catch (error) {
          logger.error('Restore error', error);
        }
      },
      onCancel: () => setConfirm({
        open: false,
        title: "",
        description: "",
        withChildren: false,
        operation: "restore"
      })
    });
  };

  const handleHardDelete = async (theme) => {
    try {
      await hardDelete(theme.id);
      notify({ type: 'success', message: t('themes.themeDeletedPermanent') });
      await refresh(true);
    } catch (error) {
      logger.error('Hard delete error', error);
    }
  };

  // Render loading state
  if (isLoading && themes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('themes.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {filter.input}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
              <span className="text-slate-500">{t('themes.loadingThemes')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (error && themes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('themes.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {filter.input}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="text-red-600 text-center">
              <p className="font-medium">{t('themes.loadingError')}</p>
              <p className="text-sm">{error}</p>
            </div>
            <Button
              onClick={() => refresh(true)}
            >
              {t('themes.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render main content
  return (
    <Card>
      <CardHeader>
        <div className="space-y-4">
          <CardTitle>{t('themes.title')}</CardTitle>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
            {!showTrash && user.role === USER_ROLES.ADMIN && (
              <Button
                onClick={openNew}
                disabled={isLoading}
                className="w-full sm:w-auto"
              >
                {t('common.new')}
              </Button>
            )}
            <div className="w-full sm:w-auto">
              {filter.input}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Error banner */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-destructive text-sm">{error}</span>
              <Button
                onClick={clearError}
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
              >
                ×
              </Button>
            </div>
          </div>
        )}

        <ResponsiveTable
          data={filteredData}
          columns={[
            { header: t('common.name'), className: "max-w-xs" },
            { header: t('themes.questions'), className: "w-24" },
            ...(isAdmin ? [{ header: t('common.linkedUser') }] : []),
            { header: t('common.actions'), className: "w-56 text-right" }
          ]}
          renderRow={(theme) => (
            <tr key={theme.id} className="border-b hover:bg-muted/50">
              <td className="py-2 max-w-xs">
                <TruncatedText text={theme.nom} maxLength={60} />
              </td>
              <td className="py-2">
                {(dataSource.state.questions || []).filter(q =>
                  q.themeId === theme.id && !q.deletedAt
                ).length}
              </td>
              {isAdmin && <td className="py-2">{userName(theme.userId)}</td>}
              <td className="py-2 text-right space-x-2">
                {!showTrash ? (
                  <>
                    {canManage(theme.userId) && (
                      <Button
                        onClick={() => openEdit(theme)}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                      >
                        {t('common.edit')}
                      </Button>
                    )}
                    {canManage(theme.userId) && (
                      <Button
                        onClick={() => handleDelete(theme)}
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={isLoading}
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {canManage(theme.userId) && (
                      <Button
                        onClick={() => handleRestore(theme)}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                      >
                        {t('common.restore')}
                      </Button>
                    )}
                    {canManage(theme.userId) && (
                      <Button
                        onClick={() => handleHardDelete(theme)}
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={isLoading}
                      >
                        {t('common.deletePermanent')}
                      </Button>
                    )}
                  </>
                )}
              </td>
            </tr>
          )}
          renderMobileCard={(theme) => (
            <div className="space-y-3 w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('common.name')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">{theme.nom}</div>
              </div>
              <div className="flex justify-between items-center w-full max-w-full">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-muted-foreground mb-1">{t('themes.questions')}</div>
                  <div className="text-sm">
                    {(dataSource.state.questions || []).filter(q =>
                      q.themeId === theme.id && !q.deletedAt
                    ).length}
                  </div>
                </div>
                {isAdmin && (
                  <div className="min-w-0 flex-1 ml-2">
                    <div className="text-sm font-medium text-muted-foreground mb-1">{t('common.linkedUser')}</div>
                    <div className="text-sm break-words">{userName(theme.userId)}</div>
                  </div>
                )}
              </div>
              {canManage(theme.userId) && (
                <div className="flex gap-2 pt-2 w-full max-w-full">
                  {!showTrash ? (
                    <>
                      <Button
                        onClick={() => openEdit(theme)}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                        className="flex-1 min-w-0"
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        onClick={() => handleDelete(theme)}
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-0 text-destructive hover:text-destructive"
                        disabled={isLoading}
                      >
                        {t('common.delete')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => handleRestore(theme)}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                        className="flex-1 min-w-0"
                      >
                        {t('common.restore')}
                      </Button>
                      <Button
                        onClick={() => handleHardDelete(theme)}
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-0 text-destructive hover:text-destructive"
                        disabled={isLoading}
                      >
                        {t('common.deletePermanent')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          emptyMessage={isLoading ? t('common.loading') : t('common.noItems')}
        />
      </CardContent>

      {/* Editor Modal */}
      <EditorModal
        open={!!editing}
        title={editing?.id ? t('themes.editTheme') : t('themes.newTheme')}
        onClose={close}
        onSubmit={handleSave}
      >
        <div className="space-y-2">
          <Label htmlFor="nom">{t('common.name')}</Label>
          <Input
            id="nom"
            name="nom"
            defaultValue={editing?.nom || ''}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contentLanguage">{t('common.contentLanguage')}</Label>
          <Select disabled={editing?.id} name="contentLanguage" defaultValue={language || 'fr'}>
            <SelectTrigger>
              <SelectValue placeholder={t('users.selectLanguage')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="pt">Português</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isAdmin ? (
          <UserSelect
            name="userId"
            label={t('themes.linkedUser')}
            users={dataSource.state.users || []}
            defaultValue={editing?.userId || preservedUserIdRef.current || user?.id}
            required={false}
          />
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="linkedUser">{t('themes.linkedUser')}</Label>
              <Input
                id="linkedUser"
                value={user?.name || ""}
                readOnly
                disabled
              />
            </div>
            <input type="hidden" name="userId" value={user?.id || ""} />
          </>
        )}
      </EditorModal>

      {/* Confirmation Modal */}
      <Confirm
        open={!!confirm.open}
        title={confirm.title}
        description={confirm.description}
        extra={
          confirm.description ? (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="withChildren"
                  checked={!!confirm.withChildren}
                  onCheckedChange={(v) => {
                    cascadeRef.current = v;
                    setConfirm(c => ({ ...c, withChildren: v }));
                  }}
                />
                <Label htmlFor="withChildren">
                  {confirm.operation === "delete"
                    ? t('themes.deleteChildrenCheckbox')
                    : t('themes.restoreChildrenCheckbox')
                  }
                </Label>
              </div>
            </div>
          ) : null
        }
        onConfirm={confirm.onConfirm}
        onCancel={confirm.onCancel}
      />
    </Card>
  );
}
